/**
 * PromptDeviceSelector
 *
 * Always-visible device selector chip in the Prompt bar, rendered next to the
 * model selector.  Powered by useUserDesktopDevices — queries by user_id only,
 * so it works regardless of which agent or chat is open.
 *
 * Hidden when no devices are connected (zero footprint, doesn't clutter the bar).
 *
 * States:
 *   loading  — query in-flight (silent, no spinner — avoids layout shift)
 *   none     — no device connected → not rendered
 *   single   — one device → green desktop icon (name on hover, read-only)
 *   multi    — 2+ devices → dropdown for per-chat selection
 *   dropped  — selected device disappeared mid-session → amber warning
 */

import { useEffect, useRef, useState } from "react";
import { ChevronDown, Monitor } from "lucide-react";
import { useUserDesktopDevices } from "../../api/queryHooks";
import useDesktopDeviceStore from "../../store/useDesktopDeviceStore";
import logger from "../../services/Logger";
import CustomTooltip from "../Tooltip/CustomTooltip";

// ── Status colors ─────────────────────────────────────────────────────────────

const ICON_GREEN = "text-ink-emerald-500 dark:text-ink-emerald-400";
const ICON_GREY  = "text-ink-gray-400 dark:text-ink-gray-500";
const ICON_AMBER = "text-ink-amber-500";

// ── Main component ────────────────────────────────────────────────────────────

/**
 * @param {string} chatId  — current chat_id; used to key selection in the store
 */
const PromptDeviceSelector = ({ chatId }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const { data: devices = [], isLoading } = useUserDesktopDevices();

  const selectedDeviceId = useDesktopDeviceStore((s) => s.selectedDevices[chatId] ?? null);
  const setSelectedDevice = useDesktopDeviceStore((s) => s.setSelectedDevice);
  const clearSelectedDevice = useDesktopDeviceStore((s) => s.clearSelectedDevice);

  // Auto-select the only device; validate existing selection on list changes
  useEffect(() => {
    if (isLoading) return;

    if (devices.length === 1) {
      if (selectedDeviceId !== devices[0].device_id) {
        setSelectedDevice(chatId, devices[0].device_id);
      }
      return;
    }

    if (devices.length === 0) {
      clearSelectedDevice(chatId);
      return;
    }

    // Multiple devices — clear stale selection if device disconnected
    if (selectedDeviceId) {
      const stillConnected = devices.some((d) => d.device_id === selectedDeviceId);
      if (!stillConnected) {
        logger.warn("PromptDeviceSelector", "Selected device disconnected", { chatId, selectedDeviceId });
        clearSelectedDevice(chatId);
      }
    }
  }, [devices, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // No devices → render nothing (don't clutter the prompt bar)
  if (!isLoading && devices.length === 0) return null;

  // Still loading first time → silent (no spinner)
  if (isLoading && devices.length === 0) return null;

  const selectedDevice = devices.find((d) => d.device_id === selectedDeviceId) ?? null;
  const displayDevice  = selectedDevice ?? devices[0] ?? null;
  const isDropped      = !isLoading && devices.length > 0 && selectedDeviceId && !selectedDevice;

  // ── Dropped ───────────────────────────────────────────────────────────────
  if (isDropped) {
    return (
      <CustomTooltip content="Device lost" side="top">
        <div className="flex items-center justify-center rounded-md p-1.5 bg-fill-hex-fffbeb dark:bg-fill-hex-78350f/20 border border-edge-amber-200 dark:border-edge-amber-700 select-none cursor-default">
          <Monitor className={`w-4 h-4 ${ICON_AMBER}`} aria-label="Device lost" />
        </div>
      </CustomTooltip>
    );
  }

  // ── Single device ─────────────────────────────────────────────────────────
  if (devices.length === 1) {
    return (
      <CustomTooltip content={`${displayDevice?.device_name ?? "My Mac"} — connected`} side="top">
        <div className="flex items-center justify-center rounded-md p-1.5 bg-fill-navigation-hover dark:bg-fill-navigation-active select-none cursor-default">
          <Monitor
            className={`w-4 h-4 ${ICON_GREEN}`}
            aria-hidden
          />
        </div>
      </CustomTooltip>
    );
  }

  // ── Multiple devices — dropdown ───────────────────────────────────────────
  return (
    <div className="relative" ref={dropdownRef}>
      <CustomTooltip content={displayDevice ? displayDevice.device_name : "Choose device"} side="top">
      <button
        type="button"
        onClick={() => setDropdownOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium bg-fill-navigation-hover dark:bg-fill-navigation-active hover:bg-fill-gray-200 dark:hover:bg-fill-sidebar-surface-dark text-ink-emerald-700 dark:text-ink-emerald-400 transition-colors duration-200"
        aria-haspopup="listbox"
        aria-expanded={dropdownOpen}
      >
        <Monitor className={`w-4 h-4 flex-shrink-0 ${displayDevice ? ICON_GREEN : ICON_GREY}`} aria-hidden />
        <span className="truncate max-w-[120px]">
          {displayDevice ? displayDevice.device_name : "Choose device"}
        </span>
        <ChevronDown
          className={`w-3 h-3 ml-0.5 text-ink-gray-500 dark:text-ink-gray-400 transition-transform duration-150 ${dropdownOpen ? "rotate-180" : ""}`}
        />
      </button>
      </CustomTooltip>

      {dropdownOpen && (
        <div
          role="listbox"
          className="absolute left-0 bottom-full mb-1.5 z-50 min-w-[200px] bg-fill-white dark:bg-fill-dialog-surface rounded-xl shadow-lg border border-edge-gray-200 dark:border-edge-panel-border py-1 overflow-hidden"
        >
          {devices.map((device) => {
            const isSelected = device.device_id === selectedDeviceId;
            return (
              <button
                key={device.device_id}
                role="option"
                aria-selected={isSelected}
                type="button"
                onClick={() => {
                  setSelectedDevice(chatId, device.device_id);
                  setDropdownOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors
                  ${isSelected
                    ? "bg-fill-emerald-50 dark:bg-fill-emerald-900/20 text-ink-emerald-700 dark:text-ink-emerald-400 font-medium"
                    : "text-ink-gray-700 dark:text-ink-gray-200 hover:bg-fill-gray-50 dark:hover:bg-fill-editor-selected-surface"}`}
              >
                <Monitor className={`w-4 h-4 flex-shrink-0 ${ICON_GREEN}`} aria-hidden />
                <span className="flex-1 truncate">{device.device_name}</span>
                {isSelected && (
                  <span className="text-[10px] text-ink-emerald-500 dark:text-ink-emerald-400 ml-1">active</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PromptDeviceSelector;
